import type {
  BoardContextImport,
  DataImportResult,
  IdeaParkingItem,
  PromiseEntry,
  StoredBoardContext,
} from "../domain/types";
import { RuleErrorCode } from "./errors";

export function markPromiseOverdue(entry: PromiseEntry, now: string): PromiseEntry {
  if (
    entry.promiseStatus === "active" &&
    new Date(entry.dueAt).getTime() < new Date(now).getTime()
  ) {
    return {
      ...entry,
      status: "overdue",
      promiseStatus: "overdue",
      updatedAt: now,
      history: [
        ...entry.history,
        {
          id: crypto.randomUUID(),
          action: "overdue",
          occurredAt: now,
        },
      ],
    };
  }
  return entry;
}

export function defaultIdeaToParking(
  candidate: Omit<IdeaParkingItem, "parkingStatus">,
): IdeaParkingItem {
  return {
    ...candidate,
    parkingStatus: "parked",
  };
}

export function detectBoardImportConflicts(
  incoming: BoardContextImport,
  existing: readonly StoredBoardContext[],
  now = new Date().toISOString(),
): DataImportResult {
  const duplicate = existing.find(
    (item) =>
      item.contractId === incoming.contract_id || item.payloadHash === incoming.payload_hash,
  );
  const sameDateConflict = existing.find(
    (item) => item.date === incoming.payload.date && item.payloadHash !== incoming.payload_hash,
  );

  if (duplicate) {
    return {
      id: crypto.randomUUID(),
      schemaVersion: "1.0.0",
      importedAt: now,
      schemaName: incoming.schema_name,
      status: "needs_confirmation",
      sourceFileName: "",
      added: 0,
      skipped: 1,
      conflicts: [
        {
          entityType: "BoardContextImport",
          incomingId: incoming.contract_id,
          localId: duplicate.id,
          reason: "duplicate",
        },
      ],
      errors: [
        {
          code: RuleErrorCode.IMPORT_DUPLICATE,
          path: "contract_id",
          message: "相同 contract_id 或 payload_hash 已导入。",
        },
      ],
    };
  }

  if (sameDateConflict) {
    return {
      id: crypto.randomUUID(),
      schemaVersion: "1.0.0",
      importedAt: now,
      schemaName: incoming.schema_name,
      status: "needs_confirmation",
      sourceFileName: "",
      added: 0,
      skipped: 0,
      conflicts: [
        {
          entityType: "BoardContextImport",
          incomingId: incoming.contract_id,
          localId: sameDateConflict.id,
          reason: "same_date_different_payload",
        },
      ],
      errors: [
        {
          code: RuleErrorCode.IMPORT_SAME_DATE_CONFLICT,
          path: "payload.date",
          message: "同一日期已有不同内容，必须保留历史并由用户选择激活版本。",
        },
      ],
    };
  }

  return {
    id: crypto.randomUUID(),
    schemaVersion: "1.0.0",
    importedAt: now,
    schemaName: incoming.schema_name,
    status: "valid",
    sourceFileName: "",
    added: 1,
    skipped: 0,
    conflicts: [],
    errors: [],
  };
}
